// ============================================================
//  Unified messaging system for the app.
//  Wrap the app in <NotifierProvider/> once (see App.js).
//  Use the hook anywhere: const notify = useNotify();
//    notify.success('Saved.')
//    notify.error('Something went wrong', { title: 'Save failed' })
//    notify.warning('Session expiring soon')
//    notify.info('New update available')
//    const ok = await notify.confirm({ title, message, severity: 'danger' })
// ============================================================

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, useMediaQuery, useTheme,
} from '@mui/material';
import {
  CheckCircleRounded, ErrorRounded, WarningAmberRounded,
  InfoRounded, Close, HelpOutlineRounded,
} from '@mui/icons-material';

// ─── Severity config ──────────────────────────────────────────
const SEVERITY = {
  success: {
    color: '#0F766E', bg: '#ECFDF5', ring: 'rgba(15,118,110,0.14)',
    icon: <CheckCircleRounded />, defaultTitle: 'Success', duration: 4000,
  },
  error: {
    color: '#B91C1C', bg: '#FEF2F2', ring: 'rgba(185,28,28,0.14)',
    icon: <ErrorRounded />, defaultTitle: 'Something went wrong', duration: 7000,
  },
  warning: {
    color: '#C89211', bg: '#FEFCE8', ring: 'rgba(200,146,17,0.16)',
    icon: <WarningAmberRounded />, defaultTitle: 'Heads up', duration: 6000,
  },
  info: {
    color: '#0B2545', bg: '#EFF6FF', ring: 'rgba(11,37,69,0.14)',
    icon: <InfoRounded />, defaultTitle: 'Notice', duration: 5000,
  },
};

const CONFIRM_SEVERITY = {
  danger:  { color: '#B91C1C', icon: <WarningAmberRounded />, tag: 'Destructive action' },
  warning: { color: '#C89211', icon: <WarningAmberRounded />, tag: 'Please confirm' },
  info:    { color: '#0B2545', icon: <InfoRounded />,          tag: 'Please confirm' },
  question:{ color: '#0B2545', icon: <HelpOutlineRounded />,   tag: 'Please confirm' },
};

// ─── Context ──────────────────────────────────────────────────
const NotifierContext = createContext(null);

let idCounter = 0;

export function NotifierProvider({ children }) {
  const [toasts, setToasts]         = useState([]);
  const [confirmState, setConfirm]  = useState(null);

  const dismiss = useCallback((id) => {
    setToasts(t => t.map(x => x.id === id ? { ...x, closing: true } : x));
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 220);
  }, []);

  const push = useCallback((severity, message, opts = {}) => {
    const cfg = SEVERITY[severity] || SEVERITY.info;
    const id  = ++idCounter;
    const duration = opts.duration != null ? opts.duration : cfg.duration;
    const toast = {
      id, severity, message,
      title: opts.title,
      action: opts.action,
      duration,
      closing: false,
    };
    setToasts(t => [toast, ...t].slice(0, 5));
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({
    success: (msg, opts) => push('success', msg, opts),
    error:   (msg, opts) => push('error',   msg, opts),
    warning: (msg, opts) => push('warning', msg, opts),
    info:    (msg, opts) => push('info',    msg, opts),
    dismiss,
    confirm: (opts) => new Promise((resolve) => {
      setConfirm({
        title: 'Are you sure?',
        message: 'This action cannot be undone.',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        severity: 'question',
        ...opts,
        _resolve: (v) => { setConfirm(null); resolve(v); },
      });
    }),
  }), [push, dismiss]);

  return (
    <NotifierContext.Provider value={api}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
      {confirmState && <ConfirmDialog {...confirmState} />}
    </NotifierContext.Provider>
  );
}

export function useNotify() {
  const ctx = useContext(NotifierContext);
  if (!ctx) throw new Error('useNotify() must be used within <NotifierProvider>');
  return ctx;
}

// ─── Toast stack (fixed, top-right) ───────────────────────────
function ToastStack({ toasts, onDismiss }) {
  return (
    <Box
      sx={{
        position: 'fixed', top: 24, right: 24,
        zIndex: 2000,
        display: 'flex', flexDirection: 'column',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
        '@media (max-width:600px)': { top: 12, right: 12, left: 12 },
      }}
    >
      {toasts.map(t => <Toast key={t.id} toast={t} onDismiss={onDismiss} />)}
    </Box>
  );
}

function Toast({ toast, onDismiss }) {
  const cfg = SEVERITY[toast.severity] || SEVERITY.info;
  return (
    <Box
      sx={{
        display: 'flex', gap: 1.5, alignItems: 'flex-start',
        p: 1.75, pr: 1, mb: 1.25,
        width: 400, maxWidth: 'calc(100vw - 32px)',
        bgcolor: '#fff', borderRadius: 2,
        borderLeft: `4px solid ${cfg.color}`,
        boxShadow: '0 12px 40px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.06)',
        position: 'relative', overflow: 'hidden',
        animation: toast.closing
          ? 'toast-out 0.22s ease-in forwards'
          : 'toast-in 0.32s cubic-bezier(0.16,1,0.3,1) forwards',
        '@keyframes toast-in': {
          '0%':   { transform: 'translateX(115%) scale(0.96)', opacity: 0 },
          '100%': { transform: 'translateX(0) scale(1)',       opacity: 1 },
        },
        '@keyframes toast-out': {
          '0%':   { transform: 'translateX(0) scale(1)',           opacity: 1 },
          '100%': { transform: 'translateX(115%) scale(0.96)',     opacity: 0 },
        },
      }}
    >
      <Box sx={{
        width: 34, height: 34, borderRadius: '50%',
        bgcolor: cfg.bg, color: cfg.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 0 0 4px ${cfg.ring}`,
        flexShrink: 0, mt: 0.25,
      }}>
        {cfg.icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Typography variant="body2" fontWeight={700} sx={{ color: '#0F172A', lineHeight: 1.25 }}>
          {toast.title || cfg.defaultTitle}
        </Typography>
        {toast.message && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, wordBreak: 'break-word' }}>
            {toast.message}
          </Typography>
        )}
        {toast.action && (
          <Button
            size="small" variant="text"
            onClick={() => { toast.action.onClick?.(); onDismiss(toast.id); }}
            sx={{
              mt: 0.75, ml: -1,
              color: cfg.color, fontWeight: 700,
              '&:hover': { bgcolor: cfg.bg },
            }}
          >
            {toast.action.label}
          </Button>
        )}
      </Box>

      <IconButton size="small" onClick={() => onDismiss(toast.id)} sx={{ mt: -0.5, mr: -0.25 }}>
        <Close fontSize="small" />
      </IconButton>

      {toast.duration > 0 && (
        <Box
          sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: 3, bgcolor: cfg.color, opacity: 0.35,
            transformOrigin: 'left center',
            animation: `toast-progress ${toast.duration}ms linear forwards`,
            '@keyframes toast-progress': {
              '0%':   { transform: 'scaleX(1)' },
              '100%': { transform: 'scaleX(0)' },
            },
          }}
        />
      )}
    </Box>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────
function ConfirmDialog({
  title, message, confirmLabel, cancelLabel, severity, _resolve,
}) {
  const cfg = CONFIRM_SEVERITY[severity] || CONFIRM_SEVERITY.question;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open onClose={() => _resolve(false)}
      maxWidth="xs" fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2, overflow: 'hidden',
          m: { xs: 2, sm: 3 },
          width: { xs: 'calc(100% - 32px)', sm: 'auto' },
        },
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center',
        gap: { xs: 1.5, sm: 2 },
        background: 'linear-gradient(90deg, #0B2545, #13315C)',
        color: '#fff',
        py: { xs: 2, sm: 2.5 },
        px: { xs: 2, sm: 3 },
      }}>
        <Box sx={{
          width: { xs: 36, sm: 42 }, height: { xs: 36, sm: 42 }, borderRadius: 1.5,
          bgcolor: cfg.color, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 6px 16px ${cfg.color}55`, flexShrink: 0,
        }}>
          {cfg.icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{
            color: 'rgba(255,255,255,0.6)', fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 10,
          }}>
            {cfg.tag}
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: { xs: 16, sm: 18 } }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, px: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        bgcolor: '#FAFBFC',
        gap: 1,
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        '& > button': { width: { xs: '100%', sm: 'auto' } },
      }}>
        <Button onClick={() => _resolve(false)} fullWidth={isXs}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained" fullWidth={isXs}
          onClick={() => _resolve(true)}
          sx={{
            bgcolor: cfg.color, color: '#fff', px: 3,
            '&:hover': { bgcolor: cfg.color, filter: 'brightness(0.92)' },
            boxShadow: `0 6px 16px ${cfg.color}44`,
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
